import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import {
  BROKERS, SYMBOLS, EMOTION_CHIPS, SOURCE_CARDS, ORDER_TYPES,
  formatMoney, estimateBrokerFees, estimateCryptoTradingFee,
  isCryptoBroker, getCryptoFeeRate, GST_RATE,
  getBrokerType, CFD_CONTRACT_SIZES, CFD_COMMISSION_PER_LOT,
  CFD_ACCOUNT_TYPES, PROP_FIRM_DEFAULT_SPLIT, DELTA_GOLD_SYMBOLS,
  getDeltaLotSize, DELTA_CONTRACT_LOT_SIZES, getRealQuantity, EXIT_TYPES,
  FUNDING_PIPS_SYMBOLS, FUNDING_PIPS_ACCOUNT_TYPES,
  FUNDING_PIPS_SYMBOL_CATEGORY, isFundingPipsBroker,
  getFundingPipsFeePerLot, estimateFundingPipsFee,
  BROKER_SYMBOLS, getBrokerSymbols,
} from "../lib/constants";
import { SearchableSelect } from "../components/SearchableSelect";

// ─── Iter 18: Per-broker Smart Defaults ───
// localStorage key. Structure: { lastBroker, brokerPrefs: { [broker]: {...} } }.
// Stored on every successful trade save; loaded on form mount + on every
// broker change so each broker "remembers" its last setup independently.
const BT_PREFS_KEY = "bt_prefs";

// ─── Iter 22: Custom symbols per broker ───
// localStorage key. Structure: { [broker]: ["BTCINR", "MOONUSDT", ...] }.
// Persisted whenever the user adds a custom pair from the symbol dropdown.
const BT_CUSTOM_SYMBOLS_KEY = "bt_custom_symbols";

function readPrefs() {
  try {
    return JSON.parse(localStorage.getItem(BT_PREFS_KEY) || "{}");
  } catch {
    return {};
  }
}

function writePrefs(next) {
  try {
    localStorage.setItem(BT_PREFS_KEY, JSON.stringify(next));
  } catch {
    /* quota / private mode — silent */
  }
}

function readCustomSymbols() {
  try {
    const v = JSON.parse(localStorage.getItem(BT_CUSTOM_SYMBOLS_KEY) || "{}");
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
}

function writeCustomSymbols(next) {
  try {
    localStorage.setItem(BT_CUSTOM_SYMBOLS_KEY, JSON.stringify(next));
  } catch {
    /* quota / private mode — silent */
  }
}
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import ScreenshotUploader from "../components/ScreenshotUploader";
import { toast } from "sonner";
import { Save, X, ChevronDown, Check } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const LEVERAGE_PRESETS = [5, 10, 25, 50, 100, 200];
const NOTES_MAX = 500;

// "#00ff88" → "rgba(0, 255, 136, alpha)" — used to compute the soft tinted
// backgrounds for selected chips / cards from their accent color.
function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Format Risk:Reward Ratio as "1 : X.X". Returns "—" when no usable value.
function formatRRR(r) {
  if (r == null || Number.isNaN(r)) return "—";
  return `1 : ${r.toFixed(1)}`;
}

// Small color-coded pill that appears next to the broker selector — helps
// users quickly understand which calculation engine drives the form.
const BROKER_BADGE_META = {
  CRYPTO:     { label: "CRYPTO",    bg: "rgba(251, 191, 36, 0.12)", color: "#fbbf24", border: "rgba(251, 191, 36, 0.35)" },
  CFD_RETAIL: { label: "CFD",       bg: "rgba(96, 165, 250, 0.12)", color: "#60a5fa", border: "rgba(96, 165, 250, 0.35)" },
  PROP_FIRM:  { label: "PROP FIRM", bg: "rgba(167, 139, 250, 0.14)", color: "#a78bfa", border: "rgba(167, 139, 250, 0.35)" },
  EQUITY:     { label: "EQUITY",    bg: "rgba(148, 163, 184, 0.12)", color: "#94a3b8", border: "rgba(148, 163, 184, 0.35)" },
};
function BrokerTypeBadge({ type }) {
  const meta = BROKER_BADGE_META[type] || BROKER_BADGE_META.EQUITY;
  return (
    <span
      data-testid={`broker-type-badge-${type}`}
      className="inline-flex items-center px-1.5 py-0.5 rounded-sm font-semibold tracking-[0.08em] uppercase"
      style={{
        fontSize: "8px",
        background: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.border}`,
        letterSpacing: "0.08em",
      }}
    >
      {meta.label}
    </span>
  );
}

// Iter 18 — Smart Defaults: small "Last used: SYM · QTY lot" hint below the
// broker selector. Lets the user see what's about to pre-fill before they
// even scroll to those fields. Hidden in edit mode and on first-time visits.
function BrokerLastUsedHint({ broker, isEdit }) {
  if (isEdit) return null;
  let bp = null;
  try {
    const prefs = JSON.parse(localStorage.getItem(BT_PREFS_KEY) || "{}");
    bp = prefs.brokerPrefs?.[broker] || null;
  } catch { bp = null; }
  if (!bp || (!bp.symbol && bp.quantity == null)) return null;
  const qtyLabel = bp.quantity != null
    ? `${bp.quantity}${getBrokerType(broker) === "CFD_RETAIL" || getBrokerType(broker) === "PROP_FIRM" ? " lot" : ""}`
    : null;
  return (
    <p
      data-testid="broker-last-used-hint"
      className="text-[11px] text-[var(--bt-text-muted)]"
    >
      Last used: <span className="text-white">{bp.symbol}</span>
      {qtyLabel && <> · <span className="text-white">{qtyLabel}</span></>}
    </p>
  );
}

export default function TradeForm() {
  const { id } = useParams(); // when editing
  const isEdit = Boolean(id);
  const nav = useNavigate();

  // Iter 18 — Smart defaults: seed initial form state from per-broker prefs on
  // fresh "Add Trade" only. Edit flow loads from the trade record below.
  const initial = useMemo(() => {
    if (isEdit) return null;
    const prefs = readPrefs();
    const broker = prefs.lastBroker && BROKERS.includes(prefs.lastBroker)
      ? prefs.lastBroker
      : "Delta Exchange";
    const bp = (prefs.brokerPrefs && prefs.brokerPrefs[broker]) || {};
    return { broker, bp };
  // We only want the *initial* prefs; subsequent changes are handled via
  // the per-broker change effect below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState({
    date: today(),
    broker: initial?.broker || "Delta Exchange",
    symbol: initial?.bp?.symbol || "BTCUSDT",
    side: initial?.bp?.side || "buy",
    entry_price: "",
    exit_price: "",
    quantity: initial?.bp?.quantity != null ? String(initial.bp.quantity) : "",
    stop_loss: "",
    order_type: initial?.bp?.orderType || "taker",
    leverage: initial?.bp?.leverage != null ? Number(initial.bp.leverage) : 10,
    trading_fees: "",
    funding_fees: "",
    other_charges: "",
    emotion: "Confident",
    source: "Own Analysis",
    notes: "",
    currency: (() => {
      try { return localStorage.getItem("bt_currency") || "USD"; } catch { return "USD"; }
    })(),
    screenshot: null,
  });
  // Iter 91 — Tracks whether the user has manually edited the
  // `trading_fees` input. When false, the field is auto-prefilled with
  // the per-broker estimate so the user SEES the exact value that will
  // be deducted (no more "blank field = auto-applied" confusion that
  // made iter-26 testers think fees were stacking). At submit time we
  // still send `null` to the backend in this case so the canonical
  // `trading_fees_auto_estimated=true` flag is set in the DB.
  const userTouchedTradingFeesRef = useRef(false);
  // When editing an existing manual trade, mark the field as already
  // touched so we don't overwrite the saved manual value with the
  // current auto-estimate on mount.
  useEffect(() => {
    if (initial?.trading_fees != null && initial?.trading_fees_auto_estimated === false) {
      userTouchedTradingFeesRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Iter 26 — Quick mode (progressive disclosure). New trades default to quick
  // mode; editing always shows everything. Preference persisted.
  const [quickMode, setQuickMode] = useState(() => {
    if (id) return false;
    try { return localStorage.getItem("bt_quick_mode") !== "false"; } catch { return true; }
  });
  useEffect(() => {
    if (isEdit) return;
    try { localStorage.setItem("bt_quick_mode", String(quickMode)); } catch { /* noop */ }
  }, [quickMode, isEdit]);
  // CFD / Prop Firm extras — seed from per-broker prefs on first mount.
  const [accountType, setAccountType] = useState(initial?.bp?.accountType || "Standard");
  const [profitSplit, setProfitSplit] = useState(
    initial?.bp?.profitSplit != null ? Number(initial.bp.profitSplit) : PROP_FIRM_DEFAULT_SPLIT
  );
  // Iter 18 — Exit discipline state.
  const [exitType, setExitType] = useState(initial?.bp?.exitType || "MANUAL");
  const [targetPrice, setTargetPrice] = useState("");
  const [exitAutoFilled, setExitAutoFilled] = useState(null); // "SL" | "TP" | null

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        // Fetch single trade directly so we get the full base64 screenshot
        // (the list endpoint strips it for payload size).
        const { data: t } = await api.get(`/trades/${id}`);
        if (t) {
          setForm({
            date: t.date,
            broker: t.broker,
            symbol: t.symbol,
            side: t.side,
            entry_price: String(t.entry_price),
            exit_price: String(t.exit_price),
            quantity: String(t.quantity),
            stop_loss: t.stop_loss != null ? String(t.stop_loss) : "",
            order_type: t.order_type || "taker",
            leverage: t.leverage != null ? Number(t.leverage) : 10,
            // For non-crypto: pre-fill manual fee unless it was auto-estimated.
            // For crypto: leave blank — backend will recompute on update.
            trading_fees: isCryptoBroker(t.broker)
              ? ""
              : (t.trading_fees_auto_estimated ? "" : (t.trading_fees != null ? String(t.trading_fees) : "")),
            funding_fees: t.funding_fees ? String(t.funding_fees) : "",
            other_charges: t.other_charges ? String(t.other_charges) : "",
            emotion: t.emotion,
            source: t.source || "Own Analysis",
            notes: t.notes || "",
            currency: t.currency || "USD",
            screenshot: t.screenshot || null,
          });
          // Restore CFD account type + Prop firm split if persisted.
          if (t.account_type) setAccountType(t.account_type);
          if (t.profit_split != null) setProfitSplit(Number(t.profit_split));
          // Iter 18 — restore exit type + target price (back-compat: defaults to MANUAL)
          setExitType(t.exit_type || "MANUAL");
          setTargetPrice(t.target_price != null ? String(t.target_price) : "");
          // Surface funding fees when editing a trade that had them
          if (t.funding_fees) setAdvancedOpen(true);
        } else {
          toast.error("Trade not found");
          nav("/trades");
        }
      } catch (e) {
        toast.error("Failed to load trade");
      }
    })();
  }, [id, isEdit, nav]);

  // Broker-type drives all conditional rendering / math below.
  // Crypto → existing engine (notional × maker/taker × GST).
  // CFD_RETAIL / PROP_FIRM → lot × contract_size; PropFirm always uses
  // commission-based account ($7/lot round trip) + a profit split slider.
  // EQUITY → existing simple per-broker estimate.
  const brokerType = getBrokerType(form.broker);
  const isCrypto = brokerType === "CRYPTO";
  const isCFD = brokerType === "CFD_RETAIL";
  const isPropFirm = brokerType === "PROP_FIRM";
  const isEquity = brokerType === "EQUITY";
  const isForex = isCFD || isPropFirm;
  const isFundingPips = isFundingPipsBroker(form.broker);
  const isGoldSymbol = DELTA_GOLD_SYMBOLS.includes(form.symbol);
  // Order Type (Taker/Maker) only matters for true crypto pairs. Delta gold
  // pairs use a flat 0.01% rate, and CFD/equity don't have the concept.
  const showOrderType = isCrypto && !isGoldSymbol;

  // Symbol universe — every broker uses BROKER_SYMBOLS (single source of truth).
  // Falls back to the full SYMBOLS list when broker has no explicit mapping.
  const availableSymbols = useMemo(
    () => getBrokerSymbols(form.broker),
    [form.broker]
  );

  // Iter 22 — Per-broker custom pairs the user has added.
  const [customSymbolsByBroker, setCustomSymbolsByBroker] = useState(() => readCustomSymbols());
  const brokerCustomSymbols = customSymbolsByBroker[form.broker] || [];

  const addCustomSymbolForBroker = (symbol) => {
    setCustomSymbolsByBroker((prev) => {
      const list = Array.isArray(prev[form.broker]) ? prev[form.broker] : [];
      if (list.includes(symbol)) return prev;
      const next = { ...prev, [form.broker]: [symbol, ...list] };
      writeCustomSymbols(next);
      return next;
    });
  };

  const pnlPreview = useMemo(() => {
    const entry = parseFloat(form.entry_price);
    const exit_p = parseFloat(form.exit_price);
    const qty = parseFloat(form.quantity);
    if (Number.isNaN(entry) || Number.isNaN(exit_p) || Number.isNaN(qty)) return null;
    const direction = form.side === "buy" ? 1 : -1;
    // Iter 65 — single-source-of-truth quantity conversion. Same helper
    // used by backend P&L / R-multiple / fee estimator, so the preview
    // here exactly matches the dashboard and Trade Doctor results.
    const baseQty = getRealQuantity(form.broker, form.symbol, qty);
    return (exit_p - entry) * baseQty * direction;
  }, [form.entry_price, form.exit_price, form.quantity, form.side, form.broker, form.symbol, isForex]);

  // Trading fee preview
  //   - Crypto broker → auto-computed from order_type + GST (always)
  //   - Non-crypto → user-entered or simple per-broker auto-estimate when blank
  const cryptoFeePreview = useMemo(() => {
    if (!isCrypto) return null;
    return estimateCryptoTradingFee(
      form.broker, form.order_type, form.entry_price, form.exit_price, form.quantity, form.symbol
    );
  }, [isCrypto, form.broker, form.order_type, form.entry_price, form.exit_price, form.quantity, form.symbol]);

  const nonCryptoEstimate = useMemo(() => {
    if (isCrypto || isForex) return null;
    return estimateBrokerFees(form.broker, form.entry_price, form.exit_price, form.quantity);
  }, [isCrypto, isForex, form.broker, form.entry_price, form.exit_price, form.quantity]);

  // Iter 91 — Prefill the `trading_fees` input with the auto-estimate so
  // the user SEES the exact value that will be deducted from net P&L.
  // Only applies to non-crypto / non-forex brokers (crypto + commission-
  // based CFD/Prop already auto-compute on the backend without any
  // user-facing field). Once the user edits the field, we stop
  // overwriting it (tracked via userTouchedTradingFeesRef).
  useEffect(() => {
    if (isCrypto || isForex) return;
    if (userTouchedTradingFeesRef.current) return;
    if (nonCryptoEstimate == null) return;
    const next = nonCryptoEstimate.toFixed(2);
    setForm((prev) => (prev.trading_fees === next ? prev : { ...prev, trading_fees: next }));
  }, [nonCryptoEstimate, isCrypto, isForex]);

  // CFD / Prop Firm auto fee:
  //   - Funding Pips Standard → category-based ($2/lot for FX/Metals/Energies, $0 for Indices/Crypto)
  //   - Funding Pips Zero → manual (returns null → manual input shown)
  //   - Other Prop Firms (FTMO, FundedNext, The5%ers) → flat $7/lot
  //   - CFD Raw / Zero → flat $7/lot. Standard / Ultra Low → manual (null).
  const cfdFeePreview = useMemo(() => {
    if (!isForex) return null;
    const lot = parseFloat(form.quantity);
    if (Number.isNaN(lot) || lot <= 0) return null;
    if (isFundingPips) {
      if (accountType === "Zero") return null; // manual entry
      return estimateFundingPipsFee(form.symbol, lot);
    }
    if (isPropFirm || accountType === "Raw / Zero") {
      return Math.round(lot * CFD_COMMISSION_PER_LOT * 100) / 100;
    }
    return null;
  }, [isForex, isPropFirm, isFundingPips, accountType, form.quantity, form.symbol]);

  // Is the trading fee field auto-driven? Crypto always; CFD/PropFirm when
  // commission-based; equity/standard CFD when the user hasn't touched
  // the input (iter-91 — value is prefilled from `nonCryptoEstimate`).
  const tradingFeeIsAuto = isCrypto
    ? true
    : isForex
      ? cfdFeePreview != null
      : !userTouchedTradingFeesRef.current;

  const effectiveTradingFee = useMemo(() => {
    if (isCrypto) return cryptoFeePreview ?? 0;
    if (isForex && cfdFeePreview != null) return cfdFeePreview;
    // Iter 91 — field is now prefilled, so the visible value IS the
    // value that gets deducted. Blank means 0 (user cleared it).
    if (form.trading_fees === "") return 0;
    const v = parseFloat(form.trading_fees);
    return Number.isNaN(v) ? 0 : v;
  }, [isCrypto, isForex, cryptoFeePreview, cfdFeePreview, form.trading_fees]);

  const effectiveFundingFee = useMemo(() => {
    const v = parseFloat(form.funding_fees);
    return Number.isNaN(v) ? 0 : v;
  }, [form.funding_fees]);

  // Iter 26 — Other Charges (broker-agnostic catch-all). Deducted from Net P&L.
  const effectiveOtherCharges = useMemo(() => {
    const v = parseFloat(form.other_charges);
    return Number.isNaN(v) ? 0 : v;
  }, [form.other_charges]);

  const totalFees = effectiveTradingFee + effectiveFundingFee + effectiveOtherCharges;

  const netPnlPreview = useMemo(() => {
    if (pnlPreview == null) return null;
    return pnlPreview - totalFees;
  }, [pnlPreview, totalFees]);

  // Prop Firm: trader receives only `profitSplit`% of NET P&L (after fees).
  // Matches backend: your_take = round(net_pnl × split / 100, 2).
  const yourTake = useMemo(() => {
    if (!isPropFirm || netPnlPreview == null) return null;
    return Math.round(netPnlPreview * (profitSplit / 100) * 100) / 100;
  }, [isPropFirm, netPnlPreview, profitSplit]);

  // Crypto fee components (pre-GST + GST) — only shown when applicable.
  // ratePct is now a decimal multiplier (e.g. 0.0005 = 0.05%) and respects
  // symbol-specific overrides (e.g. Delta gold pairs).
  const cryptoFeeBreakdown = useMemo(() => {
    if (!isCrypto || cryptoFeePreview == null) return null;
    const ratePct = getCryptoFeeRate(form.broker, form.symbol, form.order_type);
    if (ratePct == null) return null;
    const entry = parseFloat(form.entry_price);
    const exit_p = parseFloat(form.exit_price);
    const qty = parseFloat(form.quantity);
    if ([entry, exit_p, qty].some(Number.isNaN)) return null;
    // Iter 65 — single-source-of-truth qty conversion (matches backend
    // estimate_crypto_trading_fee).
    const baseQty = getRealQuantity(form.broker, form.symbol, qty);
    const notional = (entry + exit_p) * baseQty;
    const pre = notional * ratePct;
    const gst = pre * GST_RATE;
    return { notional, ratePct, pre: Math.round(pre * 100) / 100, gst: Math.round(gst * 100) / 100 };
  }, [isCrypto, cryptoFeePreview, form.broker, form.symbol, form.order_type, form.entry_price, form.exit_price, form.quantity]);

  // Risk:Reward Ratio — uses GROSS P&L per spec, displayed as "1 : X.X".
  // Risk amount scales by contract size for CFD/Prop so it mirrors the
  // backend calc_r_multiple logic.
  const rPreview = useMemo(() => {
    if (pnlPreview == null) return null;
    const entry = parseFloat(form.entry_price);
    const qty = parseFloat(form.quantity);
    const sl = parseFloat(form.stop_loss);
    if (Number.isNaN(entry) || Number.isNaN(qty) || Number.isNaN(sl) || sl === 0) return null;
    if (form.side === "buy" && sl >= entry) return null;
    if (form.side === "sell" && sl <= entry) return null;
    // Iter 65 — single-source-of-truth. Now Delta inverse-perp R-multiples
    // are correct automatically (previously only CFD got contract scaling).
    const baseQty = getRealQuantity(form.broker, form.symbol, qty);
    const risk = Math.abs(entry - sl) * baseQty;
    if (risk <= 0) return null;
    return pnlPreview / risk;
  }, [pnlPreview, form.entry_price, form.quantity, form.stop_loss, form.side, form.symbol, form.broker]);

  // Live margin calc: (Entry × baseQty) ÷ Leverage. Only meaningful on crypto.
  const marginUsed = useMemo(() => {
    const entry = parseFloat(form.entry_price);
    const qty = parseFloat(form.quantity);
    const lev = Number(form.leverage) || 1;
    if (Number.isNaN(entry) || Number.isNaN(qty) || lev <= 0) return null;
    // Iter 65 — single-source-of-truth.
    const baseQty = getRealQuantity(form.broker, form.symbol, qty);
    return (entry * baseQty) / lev;
  }, [form.entry_price, form.quantity, form.leverage, form.broker, form.symbol]);

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // ── Iter 18: re-hydrate per-broker prefs whenever the user changes broker
  // mid-form. Only runs in "new trade" mode — edit-mode loads from the trade
  // record. Loads symbol, side, qty, leverage, order/account/exit types so
  // each broker feels like its own personalised form.
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isEdit) return;
    if (isInitialRender.current) {
      // Skip first run — initial state already seeded from prefs.
      isInitialRender.current = false;
      return;
    }
    const prefs = readPrefs();
    const bp = prefs.brokerPrefs?.[form.broker];
    if (!bp) return;
    setForm((f) => ({
      ...f,
      // Only overwrite the "identity" fields — keep prices/notes untouched.
      // Restore last bp.symbol verbatim — the broker-validation effect below
      // ensures it's still valid for this broker (incl. custom pairs).
      symbol: bp.symbol || f.symbol,
      side: bp.side || f.side,
      quantity: bp.quantity != null ? String(bp.quantity) : f.quantity,
      order_type: bp.orderType || f.order_type,
      leverage: bp.leverage != null ? Number(bp.leverage) : f.leverage,
    }));
    if (bp.accountType) setAccountType(bp.accountType);
    if (bp.profitSplit != null) setProfitSplit(Number(bp.profitSplit));
    if (bp.exitType) setExitType(bp.exitType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.broker]);

  // When switching brokers, ensure the current symbol is valid for the new
  // broker. Funding Pips also enforces "Standard | Zero" account types.
  useEffect(() => {
    if (isEdit) return;
    if (isFundingPips) {
      setAccountType((at) => (at === "Standard" || at === "Zero" ? at : "Standard"));
    }
    const brokerSyms = getBrokerSymbols(form.broker);
    const customs = (customSymbolsByBroker[form.broker] || []);
    const allowed = new Set([...brokerSyms, ...customs]);
    if (!allowed.has(form.symbol)) {
      const fallback = brokerSyms[0] || customs[0] || "";
      if (fallback) {
        setForm((f) => ({ ...f, symbol: fallback }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.broker, isFundingPips, isEdit]);

  // ── Iter 18: Exit Type → auto-fill exit price ──
  // SL_HIT  → snap exit price to stop_loss (if set).
  // TP_HIT  → snap exit price to target_price (if set).
  // CUT_EARLY / MANUAL → clear the "auto-filled" hint.
  const setExitTypeAndAutoFill = (nextType) => {
    setExitType(nextType);
    if (nextType === "SL_HIT") {
      const sl = parseFloat(form.stop_loss);
      if (!Number.isNaN(sl) && sl > 0) {
        setForm((f) => ({ ...f, exit_price: String(sl) }));
        setExitAutoFilled("SL");
      } else {
        setExitAutoFilled(null);
      }
    } else if (nextType === "TP_HIT") {
      const tp = parseFloat(targetPrice);
      if (!Number.isNaN(tp) && tp > 0) {
        setForm((f) => ({ ...f, exit_price: String(tp) }));
        setExitAutoFilled("TP");
      } else {
        setExitAutoFilled(null);
      }
    } else {
      setExitAutoFilled(null);
    }
  };

  // Target Price change while TP_HIT selected → keep exit price snapped.
  const onTargetPriceChange = (v) => {
    setTargetPrice(v);
    if (exitType === "TP_HIT") {
      const tp = parseFloat(v);
      if (!Number.isNaN(tp) && tp > 0) {
        setForm((f) => ({ ...f, exit_price: String(tp) }));
        setExitAutoFilled("TP");
      }
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const cryptoBroker = isCryptoBroker(form.broker);
    const payload = {
      date: form.date,
      broker: form.broker,
      symbol: form.symbol,
      side: form.side,
      entry_price: parseFloat(form.entry_price),
      exit_price: parseFloat(form.exit_price),
      quantity: parseFloat(form.quantity),
      stop_loss: form.stop_loss === "" ? null : parseFloat(form.stop_loss),
      // Crypto: backend ignores trading_fees and recomputes from order_type.
      // Non-crypto: if the user hasn't touched the field (iter-91 prefill
      //   is in place), send null so the backend re-applies its auto
      //   estimate AND sets `trading_fees_auto_estimated=true` in the DB.
      //   If the user typed/edited (including clearing to ""), send the
      //   exact value visible in the field — never sum with the
      //   auto-estimate.
      order_type: cryptoBroker ? form.order_type : null,
      leverage: cryptoBroker ? Number(form.leverage) || null : null,
      trading_fees: cryptoBroker
        ? null
        : (!userTouchedTradingFeesRef.current
            ? null
            : (form.trading_fees === "" ? 0 : parseFloat(form.trading_fees))),
      funding_fees: form.funding_fees === "" ? 0 : parseFloat(form.funding_fees),
      other_charges: form.other_charges === "" ? 0 : parseFloat(form.other_charges),
      // CFD / Funding Pips → account_type (Standard/Ultra Low/Raw|Zero for CFD;
      // Standard/Zero for Funding Pips). Other prop firms pin Raw / Zero server-side.
      account_type: (isCFD || isFundingPips) ? accountType : null,
      profit_split: isPropFirm ? Number(profitSplit) : null,
      // Iter 18 — Exit Discipline
      exit_type: exitType || "MANUAL",
      target_price: exitType === "TP_HIT" && targetPrice !== ""
        ? parseFloat(targetPrice)
        : null,
      emotion: form.emotion,
      source: form.source,
      notes: form.notes || "",
      currency: form.currency,
      screenshot: form.screenshot || null,
    };
    if ([payload.entry_price, payload.exit_price, payload.quantity].some(Number.isNaN)) {
      setError("Entry, exit and quantity must be numeric.");
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/trades/${id}`, payload);
        toast.success("Trade updated ✅");
      } else {
        await api.post("/trades", payload);
        toast.success("Logged ✅", {
          description: "Your edge just got sharper.",
        });
      }
      // ── Iter 18: persist per-broker prefs on every successful save ──
      try {
        const prefs = readPrefs();
        prefs.lastBroker = form.broker;
        prefs.brokerPrefs = prefs.brokerPrefs || {};
        prefs.brokerPrefs[form.broker] = {
          symbol: form.symbol,
          side: form.side,
          quantity: parseFloat(form.quantity) || null,
          accountType: (isCFD || isFundingPips) ? accountType : null,
          leverage: isCryptoBroker(form.broker) ? Number(form.leverage) : null,
          orderType: isCryptoBroker(form.broker) ? form.order_type : null,
          profitSplit: isPropFirm ? Number(profitSplit) : null,
          exitType,
        };
        writePrefs(prefs);
      } catch (prefsErr) {
        // Non-fatal — quota exhausted / private browsing / Safari ITP wipe.
        // We don't surface this to the user: the trade itself saved fine.
        console.warn("Could not persist per-broker prefs:", prefsErr);
      }
      nav("/dashboard");
    } catch (err) {
      // Iter 21 — Cloudflare 520/parse errors arrive as network errors with
      // no response body. Show a user-friendly message + offer a clean retry
      // path instead of leaving the user staring at a broken page.
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      const msg = detail
        || (err.code === "ECONNABORTED" || !err.response
          ? "Trade could not be saved — network or server hiccup. Please try again."
          : err.message);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11 rounded-sm";
  const selectTriggerCls = "bg-transparent border-[var(--bt-border)] data-[placeholder]:text-[var(--bt-text-muted)] focus:border-[var(--bt-accent)] focus:ring-1 focus:ring-[var(--bt-accent)] h-11 rounded-sm";

  return (
    <div className="space-y-6" data-testid="trade-form-page">
      <div>
        <p className="bt-label">{isEdit ? "Edit" : "New Entry"}</p>
        <h1 className="font-heading text-4xl">{isEdit ? "Edit Trade" : "Log a Trade"}</h1>
        <p className="text-sm text-[var(--bt-text-muted)] mt-1">
          Every entry sharpens your edge. P&L is calculated automatically.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        data-testid="trade-form"
        className="bt-card bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 lg:p-8 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* ─── Quick fields (always visible) — Broker, Symbol, Side, Entry, Exit, Quantity ─── */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="bt-label">Broker</Label>
              <BrokerTypeBadge type={brokerType} />
            </div>
            <SearchableSelect
              testId="trade-broker-select"
              value={form.broker}
              onChange={setField("broker")}
              options={BROKERS}
              placeholder="Select broker…"
              searchPlaceholder="Search brokers…"
              emptyText="No matching broker"
            />
            {/* Iter 18 — Smart Defaults "Last used" hint */}
            <BrokerLastUsedHint broker={form.broker} isEdit={isEdit} />
          </div>
          <div className="space-y-2">
            <Label className="bt-label">Symbol</Label>
            <SearchableSelect
              testId="trade-symbol-select"
              value={form.symbol}
              onChange={setField("symbol")}
              options={availableSymbols}
              customOptions={brokerCustomSymbols}
              placeholder="Select symbol…"
              searchPlaceholder="Search symbols…"
              emptyText="No matching symbol"
              showAddCustom={true}
              addCustomLabel="Add Pair"
              onAddCustom={addCustomSymbolForBroker}
            />
            {form.broker === "Delta Exchange" &&
              DELTA_CONTRACT_LOT_SIZES[form.symbol] !== undefined &&
              DELTA_CONTRACT_LOT_SIZES[form.symbol] !== 1 && (
                <p
                  data-testid="delta-lot-size-hint"
                  className="text-[11px] text-[var(--bt-text-muted)] font-mono"
                >
                  1 contract = {DELTA_CONTRACT_LOT_SIZES[form.symbol]}{" "}
                  {form.symbol.replace(/USD[T]?$/, "")}
                </p>
              )}
          </div>
          <div className="space-y-2">
            <Label className="bt-label">Side</Label>
            <Select value={form.side} onValueChange={setField("side")}>
              <SelectTrigger data-testid="trade-side-select" className={selectTriggerCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="buy">BUY (Long)</SelectItem>
                <SelectItem value="sell">SELL (Short)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="bt-label">{isForex ? "Lot Size" : "Quantity"}</Label>
            <Input
              type="number"
              step={isForex ? "0.01" : "any"}
              min={isForex ? "0.01" : "0"}
              value={form.quantity}
              onChange={(e) => setField("quantity")(e.target.value)}
              required
              placeholder={isForex ? "0.10" : "1"}
              data-testid="trade-qty-input"
              className={`${inputCls} bt-num`}
            />
            {isForex && (
              <p
                data-testid="lot-size-helper"
                className="text-[11px] text-[var(--bt-text-muted)]"
              >
                Standard = 1.0 · Mini = 0.1 · Micro = 0.01
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="bt-label">Entry Price</Label>
            <Input
              type="number" step="any" min="0"
              value={form.entry_price}
              onChange={(e) => setField("entry_price")(e.target.value)}
              required placeholder="0.00"
              data-testid="trade-entry-input"
              className={`${inputCls} bt-num`}
            />
          </div>
          <div className="space-y-2">
            <Label className="bt-label">Exit Price</Label>
            <Input
              type="number" step="any" min="0"
              value={form.exit_price}
              onChange={(e) => {
                setField("exit_price")(e.target.value);
                // Manual override clears the "auto-filled" hint.
                if (exitAutoFilled) setExitAutoFilled(null);
              }}
              required placeholder="0.00"
              data-testid="trade-exit-input"
              className={`${inputCls} bt-num`}
            />
            {exitAutoFilled === "SL" && (
              <p
                data-testid="exit-auto-fill-hint"
                className="text-[11px] font-medium"
                style={{ color: "#ef4444" }}
              >
                ↑ Auto-filled from your Stop Loss
              </p>
            )}
            {exitAutoFilled === "TP" && (
              <p
                data-testid="exit-auto-fill-hint"
                className="text-[11px] font-medium"
                style={{ color: "var(--bt-accent)" }}
              >
                ↑ Auto-filled from your Target
              </p>
            )}
          </div>
        </div>

        {/* ─── Iter 26: "+ Add Details" toggle (hidden on edit — edit always shows everything) ─── */}
        {!isEdit && (
          <div className="flex justify-center">
            <button
              type="button"
              data-testid="toggle-quick-mode"
              onClick={() => setQuickMode((q) => !q)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-[var(--bt-border)] bg-[var(--bt-surface-2)] hover:border-[var(--bt-accent)] hover:text-[var(--bt-accent)] transition-all duration-150"
              aria-expanded={!quickMode}
            >
              <span aria-hidden="true">{quickMode ? "+" : "−"}</span>
              {quickMode ? "Add Details" : "Hide Details"}
              <span className="text-[10px] text-[var(--bt-text-muted)] font-normal">
                ({quickMode ? "Date · Stop · Target · Fees · Notes" : "showing all"})
              </span>
            </button>
          </div>
        )}

        {/* ─── Iter 26: Advanced fields (smooth collapse) — Date, Currency, Exit Type,
             Account Type, Profit Split, Fees, Emotion, Source, Screenshot, Notes ─── */}
        <div
          data-testid="advanced-section"
          aria-hidden={quickMode && !isEdit}
          className={`bt-collapse ${quickMode && !isEdit ? "" : "open"}`}
        >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label className="bt-label">Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setField("date")(e.target.value)}
              required
              data-testid="trade-date-input"
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label className="bt-label">Price Currency</Label>
            <Select value={form.currency} onValueChange={setField("currency")}>
              <SelectTrigger data-testid="trade-currency-select" className={selectTriggerCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="INR">INR (₹)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* ─── Iter 18: Exit Type tagger ─── */}
          <div className="space-y-2 md:col-span-2" data-testid="exit-type-row">
            <Label className="bt-label">Exit Type</Label>
            <div className="flex flex-wrap gap-2" data-testid="exit-type-chips">
              {EXIT_TYPES.map((et) => {
                const selected = exitType === et.value;
                return (
                  <button
                    key={et.value}
                    type="button"
                    data-testid={`exit-type-${et.value}`}
                    aria-pressed={selected}
                    onClick={() => setExitTypeAndAutoFill(et.value)}
                    className="px-4 py-2 rounded-full text-[13px] border transition-all duration-150 ease-out focus:outline-none focus-visible:ring-2"
                    style={
                      selected
                        ? {
                            background: `${et.color}20`,
                            borderColor: et.color,
                            color: et.color,
                            fontWeight: 600,
                            boxShadow: `0 0 0 1px ${et.color}`,
                          }
                        : {
                            background: "var(--bt-surface-hover)",
                            borderColor: "var(--bt-border)",
                            color: "var(--bt-text-muted)",
                            fontWeight: 500,
                          }
                    }
                  >
                    <span className="mr-1.5">{et.emoji}</span>
                    {et.label}
                  </button>
                );
              })}
            </div>
            {exitType === "TP_HIT" && (
              <div className="mt-3 space-y-2" data-testid="target-price-row">
                <Label className="bt-label" style={{ color: "var(--bt-accent)" }}>
                  Target Price
                </Label>
                <Input
                  type="number" step="any" min="0"
                  value={targetPrice}
                  onChange={(e) => onTargetPriceChange(e.target.value)}
                  placeholder="0.00"
                  data-testid="trade-target-price-input"
                  className={`${inputCls} bt-num`}
                />
                <p className="text-[11px] text-[var(--bt-text-muted)]">
                  What was your original target? Auto-fills Exit Price above.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label className="bt-label">
              <span className="text-base">📝</span> Emotion Tag
            </Label>
            <div
              data-testid="trade-emotion-chips"
              className="grid grid-cols-3 gap-2"
            >
              {EMOTION_CHIPS.map((chip) => {
                const selected = form.emotion === chip.id;
                return (
                  <button
                    key={chip.id}
                    type="button"
                    data-testid={`emotion-chip-${chip.id}`}
                    aria-pressed={selected}
                    onClick={() => setField("emotion")(chip.id)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-[13px] border transition-all duration-150 ease-out hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0"
                    style={
                      selected
                        ? {
                            background: hexToRgba(chip.color, 0.15),
                            borderColor: chip.color,
                            color: "#ffffff",
                            fontWeight: 600,
                            boxShadow: `0 0 0 1px ${chip.color}`,
                          }
                        : {
                            background: "var(--bt-surface-hover)",
                            borderColor: "var(--bt-border)",
                            color: "var(--bt-text-muted)",
                            fontWeight: 500,
                          }
                    }
                  >
                    <span aria-hidden className="text-base leading-none">{chip.emoji}</span>
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-3 md:col-span-2">
            <Label className="bt-label">
              Trade Source <span className="text-[var(--bt-accent)] normal-case tracking-normal">*</span>
            </Label>
            <div
              data-testid="trade-source-cards"
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              {SOURCE_CARDS.map((card) => {
                const selected = form.source === card.id;
                return (
                  <button
                    key={card.id}
                    type="button"
                    data-testid={`source-card-${card.id}`}
                    aria-pressed={selected}
                    onClick={() => setField("source")(card.id)}
                    className="relative text-left rounded-md p-4 transition-all duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--bt-accent)] hover:[transform:scale(1.01)]"
                    style={
                      selected
                        ? {
                            background: hexToRgba(card.color, 0.12),
                            border: "1px solid #2a2a2a",
                            borderLeft: `4px solid ${card.color}`,
                            opacity: 1,
                          }
                        : {
                            background: "var(--bt-surface-hover)",
                            border: "1px solid var(--bt-border)",
                            borderLeft: `4px solid ${card.color}40`,
                            opacity: 0.7,
                          }
                    }
                    onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "var(--bt-border)"; }}
                    onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "var(--bt-surface-hover)"; }}
                  >
                    {selected && (
                      <span
                        data-testid={`source-card-check-${card.id}`}
                        className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full"
                        style={{ background: card.color, color: "#000000" }}
                      >
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                    )}
                    <div className="flex items-start gap-3">
                      <span aria-hidden className="text-2xl leading-none mt-0.5">{card.emoji}</span>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-white leading-tight">{card.label}</p>
                        <p className="text-[11px] text-[#94a3b8] mt-1 leading-snug">{card.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] italic text-[#94a3b8]">
              Your selection powers the Signal vs Self Tracker on your dashboard.
            </p>
          </div>

          {/* ===== Fee Engine — Crypto auto / CFD-Forex / Equity manual ===== */}
          {isCrypto ? (
            <>
              {showOrderType ? (
                <div
                  data-testid="sl-ot-row"
                  className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <div className="space-y-2 flex flex-col">
                    <Label className="bt-label">
                      Stop Loss{" "}
                      <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">
                        (optional · enables Risk:Reward)
                      </span>
                    </Label>
                    <Input
                      type="number" step="any" min="0"
                      value={form.stop_loss}
                      onChange={(e) => setField("stop_loss")(e.target.value)}
                      placeholder="—"
                      data-testid="trade-sl-input"
                      className={`${inputCls} bt-num`}
                    />
                  </div>
                  <div className="space-y-2 flex flex-col">
                    <Label className="bt-label">
                      Order Type{" "}
                      <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">
                        (crypto)
                      </span>
                    </Label>
                    <Select value={form.order_type} onValueChange={setField("order_type")}>
                      <SelectTrigger data-testid="trade-order-type-select" className={selectTriggerCls}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ORDER_TYPES.map((o) => {
                          const r = getCryptoFeeRate(form.broker, form.symbol, o);
                          const pct = r != null ? (r * 100).toString() : "0";
                          return (
                            <SelectItem key={o} value={o}>
                              {o === "taker" ? "Taker" : "Maker"} ({pct}%)
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 md:col-span-2">
                  <Label className="bt-label">
                    Stop Loss{" "}
                    <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">
                      (optional · enables Risk:Reward)
                    </span>
                  </Label>
                  <Input
                    type="number" step="any" min="0"
                    value={form.stop_loss}
                    onChange={(e) => setField("stop_loss")(e.target.value)}
                    placeholder="—"
                    data-testid="trade-sl-input"
                    className={`${inputCls} bt-num`}
                  />
                  <p
                    data-testid="gold-flat-rate-hint"
                    className="text-[11px] text-[var(--bt-text-muted)]"
                  >
                    Gold pairs use a flat 0.01% maker/taker rate · order type
                    not required.
                  </p>
                </div>
              )}

              {/* ===== Leverage (crypto only) ===== */}
              <div
                data-testid="trade-leverage-field"
                className="md:col-span-2 space-y-3 border border-dashed border-[var(--bt-border)] rounded-sm p-4"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <Label className="bt-label">Leverage</Label>
                    <p className="text-xs text-[var(--bt-text-muted)] mt-1">
                      Drag the slider or pick a preset · 1× to 200×
                    </p>
                  </div>
                  <div
                    data-testid="trade-leverage-value"
                    className="font-heading text-3xl font-bold bt-num"
                    style={{ color: "#00ff88" }}
                  >
                    {form.leverage}x
                  </div>
                </div>
                <Slider
                  data-testid="trade-leverage-slider"
                  value={[Number(form.leverage) || 1]}
                  min={1}
                  max={200}
                  step={1}
                  onValueChange={(vals) => setField("leverage")(vals[0])}
                  className="w-full"
                />
                <div data-testid="trade-leverage-presets" className="flex flex-wrap gap-2">
                  {LEVERAGE_PRESETS.map((p) => {
                    const selected = Number(form.leverage) === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        data-testid={`trade-leverage-preset-${p}`}
                        onClick={() => setField("leverage")(p)}
                        className="px-3 py-1.5 rounded-sm text-sm font-mono border transition-all duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00ff88]"
                        style={
                          selected
                            ? {
                                background: "#00ff88",
                                color: "#000000",
                                fontWeight: 700,
                                borderColor: "#00ff88",
                                boxShadow: "0 0 12px rgba(0, 255, 136, 0.4)",
                              }
                            : {
                                background: "var(--bt-surface-hover)",
                                border: "1px solid var(--bt-border)",
                                color: "var(--bt-text-muted)",
                              }
                        }
                        onMouseEnter={(e) => {
                          if (!selected) {
                            e.currentTarget.style.borderColor = "#00ff88";
                            e.currentTarget.style.color = "var(--bt-text)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selected) {
                            e.currentTarget.style.borderColor = "var(--bt-border)";
                            e.currentTarget.style.color = "var(--bt-text-muted)";
                          }
                        }}
                      >
                        {p}x
                      </button>
                    );
                  })}
                </div>
                {Number(form.leverage) >= 100 && (
                  <p
                    data-testid="leverage-high-warning"
                    className="text-[11px] flex items-center gap-1"
                    style={{ color: "#fbbf24" }}
                  >
                    ⚠️ High leverage — increased liquidation risk.
                  </p>
                )}
                <p
                  data-testid="trade-margin-used"
                  className="text-sm text-[var(--bt-text-muted)] pt-2 border-t border-dashed border-[var(--bt-border)]"
                >
                  Margin Used:{" "}
                  <span className="font-mono text-white">
                    {marginUsed == null ? "—" : formatMoney(marginUsed, form.currency)}
                  </span>
                </p>
              </div>

              <div className="md:col-span-2 border border-dashed border-[var(--bt-accent)]/40 bg-[var(--bt-accent)]/5 rounded-sm p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="bt-label" style={{ color: "var(--bt-accent)" }}>
                      Auto Trading Fee · {form.broker}
                    </p>
                    <p className="text-xs text-[var(--bt-text-muted)] mt-1">
                      Computed from notional × {form.order_type} rate × (1 + 18% GST)
                    </p>
                  </div>
                  <div className="text-right">
                    <div
                      data-testid="crypto-fee-auto-value"
                      className="font-heading text-2xl bt-num text-[var(--bt-accent)]"
                    >
                      {cryptoFeePreview == null ? "—" : formatMoney(cryptoFeePreview, form.currency)}
                    </div>
                    {cryptoFeeBreakdown && (
                      <p
                        data-testid="crypto-fee-breakdown"
                        className="text-[11px] font-mono text-[var(--bt-text-muted)] mt-1"
                      >
                        notional {formatMoney(cryptoFeeBreakdown.notional, form.currency)} ×{" "}
                        {(cryptoFeeBreakdown.ratePct * 100).toString()}% = {formatMoney(cryptoFeeBreakdown.pre, form.currency)} +{" "}
                        GST {formatMoney(cryptoFeeBreakdown.gst, form.currency)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* ===== Advanced (collapsed by default) — Funding Fees lives here ===== */}
              <div className="md:col-span-2">
                <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                  <CollapsibleTrigger
                    data-testid="trade-advanced-toggle"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.12em] uppercase text-[var(--bt-text-muted)] hover:text-white transition-colors"
                  >
                    Advanced
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-200 ${advancedOpen ? "rotate-180" : ""}`}
                    />
                    <span className="normal-case tracking-normal text-[var(--bt-text-muted)]/70 font-normal ml-0.5">
                      (Optional)
                    </span>
                  </CollapsibleTrigger>
                  <CollapsibleContent
                    data-testid="trade-advanced-content"
                    className="mt-3 space-y-2"
                  >
                    <Label className="bt-label">
                      Funding Fees{" "}
                      <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">
                        (crypto perpetuals)
                      </span>
                    </Label>
                    <Input
                      type="number" step="any" min="0"
                      value={form.funding_fees}
                      onChange={(e) => setField("funding_fees")(e.target.value)}
                      placeholder="0.00"
                      data-testid="trade-funding-fees-input"
                      className={`${inputCls} bt-num`}
                    />
                    <p className="text-xs text-[var(--bt-text-muted)]">
                      Perpetual funding paid/received during the trade. Subtracted from Net P&L.
                    </p>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </>
          ) : isForex ? (
            <>
              {/* ===== CFD / Forex / Prop Firm — lot-based engine ===== */}
              <div className="space-y-2 md:col-span-2">
                <Label className="bt-label">
                  Stop Loss{" "}
                  <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">
                    (optional · enables Risk:Reward)
                  </span>
                </Label>
                <Input
                  type="number" step="any" min="0"
                  value={form.stop_loss}
                  onChange={(e) => setField("stop_loss")(e.target.value)}
                  placeholder="—"
                  data-testid="trade-sl-input"
                  className={`${inputCls} bt-num`}
                />
              </div>

              {/* Account Type — CFD retail + Funding Pips */}
              {(isCFD || isFundingPips) && (
                <div
                  data-testid={isFundingPips ? "funding-pips-account-type-row" : "cfd-account-type-row"}
                  className="md:col-span-2 space-y-2"
                >
                  <Label className="bt-label">
                    Account Type{" "}
                    <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">
                      ({isFundingPips ? "Funding Pips" : "CFD"})
                    </span>
                  </Label>
                  <div
                    data-testid={isFundingPips ? "funding-pips-account-type-chips" : "cfd-account-type-chips"}
                    className="flex flex-wrap gap-2"
                  >
                    {(isFundingPips ? FUNDING_PIPS_ACCOUNT_TYPES : CFD_ACCOUNT_TYPES).map((t) => {
                      const selected = accountType === t;
                      const testIdPrefix = isFundingPips ? "funding-pips-account-type" : "cfd-account-type";
                      return (
                        <button
                          key={t}
                          type="button"
                          data-testid={`${testIdPrefix}-${t.replace(/[^a-zA-Z0-9]/g, "-")}`}
                          aria-pressed={selected}
                          onClick={() => setAccountType(t)}
                          className="px-4 py-2 rounded-full text-[13px] border transition-all duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--bt-accent)]"
                          style={
                            selected
                              ? {
                                  background: "rgba(0, 255, 136, 0.12)",
                                  borderColor: "#00ff88",
                                  color: "#ffffff",
                                  fontWeight: 600,
                                  boxShadow: "0 0 0 1px #00ff88",
                                }
                              : {
                                background: "var(--bt-surface-hover)",
                                borderColor: "var(--bt-border)",
                                color: "var(--bt-text-muted)",
                                  fontWeight: 500,
                                }
                          }
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-[var(--bt-text-muted)]">
                    {isFundingPips
                      ? <>Standard accounts pay <span className="font-mono">$2/lot</span> on Forex/Metals/Energies and <span className="font-mono">$0</span> on Indices/Crypto. Zero accounts have wider spreads — enter commission manually.</>
                      : <>Raw / Zero accounts pay a flat ${CFD_COMMISSION_PER_LOT.toFixed(2)} commission per lot
                    (round trip) — Standard / Ultra Low bake the cost into the
                    spread.</>}
                  </p>
                </div>
              )}

              {/* Trading fee — auto on commission-based accounts, manual otherwise */}
              {cfdFeePreview != null ? (
                <div
                  data-testid={isFundingPips ? "funding-pips-fee-auto" : "cfd-fee-auto"}
                  className="md:col-span-2 border border-dashed border-[var(--bt-accent)]/40 bg-[var(--bt-accent)]/5 rounded-sm p-4"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="bt-label" style={{ color: "var(--bt-accent)" }}>
                        {isFundingPips ? (
                          <>
                            Commission · ${getFundingPipsFeePerLot(form.symbol).toFixed(2)}/lot ·{" "}
                            {FUNDING_PIPS_SYMBOL_CATEGORY[form.symbol] || "—"} · auto
                          </>
                        ) : (
                          <>Commission · ${CFD_COMMISSION_PER_LOT.toFixed(2)}/lot round trip · auto</>
                        )}
                      </p>
                      <p className="text-xs text-[var(--bt-text-muted)] mt-1">
                        {parseFloat(form.quantity) || 0} lot{(parseFloat(form.quantity) || 0) === 1 ? "" : "s"}{" "}
                        × ${(isFundingPips ? getFundingPipsFeePerLot(form.symbol) : CFD_COMMISSION_PER_LOT).toFixed(2)} ={" "}
                        {formatMoney(cfdFeePreview, form.currency)}
                      </p>
                    </div>
                    <div className="text-right">
                      <div
                        data-testid={isFundingPips ? "funding-pips-fee-auto-value" : "cfd-fee-auto-value"}
                        className="font-heading text-2xl bt-num text-[var(--bt-accent)]"
                      >
                        {formatMoney(cfdFeePreview, form.currency)}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 md:col-span-2">
                  <Label className="bt-label">
                    Trading Fee{" "}
                    <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">
                      ({isFundingPips ? "commission · manual" : "spread cost · manual"})
                    </span>
                  </Label>
                  <Input
                    type="number" step="any" min="0"
                    value={form.trading_fees}
                    onChange={(e) => {
                      // Iter 91 — Same override-not-stack semantic as the
                      // equity branch: typed value is the ONLY amount
                      // deducted (CFD Standard / Funding Pips Zero have
                      // no auto-estimate, so this branch never stacks).
                      userTouchedTradingFeesRef.current = true;
                      setField("trading_fees")(e.target.value);
                    }}
                    placeholder="0.00"
                    data-testid="trade-fees-input"
                    className={`${inputCls} bt-num`}
                  />
                  <p className="text-xs text-[var(--bt-text-muted)]">
                    {isFundingPips
                      ? "Zero accounts have wider spreads + variable commission per challenge tier. Enter your effective commission for this trade."
                      : "Enter your spread cost manually. Standard / Ultra Low accounts bake the fee into the spread — there's no separate commission."}
                  </p>
                </div>
              )}

              {/* Profit Split — Prop Firm only */}
              {isPropFirm && (
                <div
                  data-testid="prop-firm-split"
                  className="md:col-span-2 space-y-3 border border-dashed border-[var(--bt-border)] rounded-sm p-4"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <Label className="bt-label">Profit Split</Label>
                      <p className="text-xs text-[var(--bt-text-muted)] mt-1">
                        Drag the slider · 70% to 90%
                      </p>
                    </div>
                    <div
                      data-testid="prop-firm-split-value"
                      className="font-heading text-3xl font-bold bt-num"
                      style={{ color: "#a78bfa" }}
                    >
                      {profitSplit}%
                    </div>
                  </div>
                  <Slider
                    data-testid="prop-firm-split-slider"
                    value={[profitSplit]}
                    min={70}
                    max={90}
                    step={5}
                    onValueChange={(vals) => setProfitSplit(vals[0])}
                    className="w-full"
                  />
                  <p
                    data-testid="prop-firm-split-meta"
                    className="text-xs text-[var(--bt-text-muted)] font-mono"
                  >
                    Your share: <span className="text-white">{profitSplit}%</span>{" "}
                    · Firm&apos;s share: <span className="text-white">{100 - profitSplit}%</span>
                  </p>
                  <div className="pt-3 border-t border-dashed border-[var(--bt-border)] flex items-end justify-between gap-3 flex-wrap">
                    <div>
                      <p className="bt-label" style={{ color: "#a78bfa" }}>
                        Your Take (after split)
                      </p>
                      <p className="text-[11px] text-[var(--bt-text-muted)] mt-1">
                        = Gross P&L × {profitSplit}%
                      </p>
                    </div>
                    <div
                      data-testid="prop-firm-your-take"
                      className={`font-heading text-3xl bt-num font-bold ${
                        yourTake == null
                          ? "text-[var(--bt-text-muted)]"
                          : yourTake >= 0
                            ? "text-[var(--bt-accent)]"
                            : "text-[var(--bt-danger)]"
                      }`}
                    >
                      {yourTake == null ? "—" : formatMoney(yourTake, form.currency)}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="space-y-2 md:col-span-2">
                <Label className="bt-label">
                  Stop Loss{" "}
                  <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">
                    (optional · enables Risk:Reward)
                  </span>
                </Label>
                <Input
                  type="number" step="any" min="0"
                  value={form.stop_loss}
                  onChange={(e) => setField("stop_loss")(e.target.value)}
                  placeholder="—"
                  data-testid="trade-sl-input"
                  className={`${inputCls} bt-num`}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <Label className="bt-label">
                    Total Fees / Charges{" "}
                    <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">
                      (optional · auto-estimated if blank)
                    </span>
                  </Label>
                  {nonCryptoEstimate != null && (
                    <span
                      data-testid="fees-estimate-hint"
                      className="text-xs font-mono text-[var(--bt-text-muted)]"
                    >
                      Auto for <span className="text-white">{form.broker}</span>:{" "}
                      <span className="text-[var(--bt-accent)] font-semibold">
                        {formatMoney(nonCryptoEstimate, form.currency)}
                      </span>
                    </span>
                  )}
                </div>
                <Input
                  type="number" step="any" min="0"
                  value={form.trading_fees}
                  onChange={(e) => {
                    // Iter 91 — Once the user types (or clears) the field,
                    // we stop auto-prefilling and send the visible value
                    // verbatim to the backend. This guarantees no
                    // double-counting with the auto-estimate — the field
                    // IS the value that gets deducted.
                    userTouchedTradingFeesRef.current = true;
                    setField("trading_fees")(e.target.value);
                  }}
                  placeholder={
                    nonCryptoEstimate != null
                      ? `Auto: ${formatMoney(nonCryptoEstimate, form.currency)}`
                      : "0.00 — fill entry/exit/qty to see auto-estimate"
                  }
                  data-testid="trade-fees-input"
                  className={`${inputCls} bt-num`}
                />
                <p className="text-xs text-[var(--bt-text-muted)]">
                  {tradingFeeIsAuto && nonCryptoEstimate != null
                    ? `Pre-filled with the ${form.broker} auto-estimate. Edit if your broker charged differently — the value you see is the ONLY amount deducted from Net P&L (never the auto-estimate plus your edit).`
                    : "Total of brokerage, taxes, slippage. The value you see is the ONLY amount deducted from Net P&L."}
                </p>
              </div>
            </>
          )}

          {/* Iter 26 — Other Charges (broker-agnostic catch-all: swap fees,
              slippage, taxes etc.). Deducted from Net P&L like funding_fees. */}
          <div className="space-y-2 md:col-span-2">
            <Label className="bt-label">
              Other Charges{" "}
              <span className="text-[var(--bt-text-muted)] normal-case tracking-normal">
                (optional · swap, slippage, taxes — deducted from Net P&L)
              </span>
            </Label>
            <Input
              type="number" step="any" min="0"
              value={form.other_charges}
              onChange={(e) => setField("other_charges")(e.target.value)}
              placeholder="0.00"
              data-testid="trade-other-charges-input"
              className={`${inputCls} bt-num`}
            />
          </div>

          {/* CHANGE: Screenshot uploader — sits above the journal/notes block */}
          <ScreenshotUploader
            value={form.screenshot}
            onChange={(v) => setField("screenshot")(v)}
          />

          {/* CHANGE 5: Premium journal-style Notes with auto-grow + char counter */}
          <div className="space-y-2 md:col-span-2">
            <div className="flex items-center justify-between gap-3">
              <Label
                data-testid="notes-header"
                className="font-semibold text-white normal-case tracking-normal text-sm"
              >
                <span className="text-base mr-1">📝</span> Trade Journal
              </Label>
              <span
                data-testid="notes-char-counter"
                className="text-xs font-mono"
                style={{
                  color: (form.notes?.length ?? 0) >= 400 ? "#fbbf24" : "#94a3b8",
                  transition: "color 200ms ease",
                }}
              >
                {form.notes?.length ?? 0} / {NOTES_MAX}
              </span>
            </div>
            <textarea
              data-testid="trade-notes-input"
              value={form.notes}
              maxLength={NOTES_MAX}
              onChange={(e) => {
                setField("notes")(e.target.value);
                // Auto-grow textarea
                e.target.style.height = "auto";
                e.target.style.height = `${Math.min(e.target.scrollHeight, 360)}px`;
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#00ff88";
                e.target.style.boxShadow = "0 0 0 3px rgba(0, 255, 136, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#1a2e1f";
                e.target.style.boxShadow = "none";
              }}
              placeholder="What was your thesis? Did you follow your plan? What would you do differently?"
              className="w-full italic-placeholder font-sans text-sm leading-relaxed text-white outline-none resize-none"
              style={{
                background: "#0f1a14",
                border: "1px solid #1a2e1f",
                borderRadius: "12px",
                padding: "16px",
                minHeight: "120px",
                transition: "border-color 200ms ease, box-shadow 200ms ease",
                fontStyle: "normal",
              }}
              rows={5}
            />
          </div>
        </div>
        </div>{/* /bt-collapse advanced section */}

        {/* P&L preview — Net P&L is the main figure; Gross + Trading + Funding + R shown below */}
        <div
          data-testid="pnl-preview"
          className="border border-dashed border-[var(--bt-border)] rounded-sm p-5 space-y-4"
        >
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="bt-label">Net P&L</p>
              <p className="text-xs text-[var(--bt-text-muted)] mt-1">
                After fees · in {form.currency}
              </p>
            </div>
            <div className="text-right">
              <div
                data-testid="net-pnl-preview-value"
                className={`font-heading text-4xl bt-num font-bold ${
                  netPnlPreview == null ? "text-[var(--bt-text-muted)]"
                  : netPnlPreview >= 0 ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"
                }`}
              >
                {netPnlPreview == null ? "—" : formatMoney(netPnlPreview, form.currency)}
              </div>
              {pnlPreview != null && Math.abs(pnlPreview) > 0 && totalFees > 0 && (
                <p
                  data-testid="fees-drag-hint"
                  className="text-xs text-[var(--bt-text-muted)] mt-1"
                >
                  fees ate{" "}
                  <span className="font-mono text-[var(--bt-danger)]">
                    {((totalFees / Math.abs(pnlPreview)) * 100).toFixed(1)}%
                  </span>{" "}
                  of gross
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-dashed border-[var(--bt-border)]">
            <div>
              <p className="bt-label">Gross P&L</p>
              <div
                data-testid="gross-pnl-preview-value"
                className={`font-heading text-lg bt-num mt-1 ${
                  pnlPreview == null ? "text-[var(--bt-text-muted)]"
                  : pnlPreview >= 0 ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"
                }`}
              >
                {pnlPreview == null ? "—" : formatMoney(pnlPreview, form.currency)}
              </div>
              <p className="text-[10px] text-[var(--bt-text-muted)] mt-0.5">Before fees</p>
            </div>
            <div>
              <p className="bt-label">Trading Fee</p>
              <div
                data-testid="trading-fee-preview-value"
                className={`font-heading text-lg bt-num mt-1 ${
                  effectiveTradingFee > 0 ? "text-[var(--bt-danger)]" : "text-[var(--bt-text-muted)]"
                }`}
              >
                {effectiveTradingFee > 0
                  ? `-${formatMoney(effectiveTradingFee, form.currency)}`
                  : "—"}
              </div>
              <p className="text-[10px] text-[var(--bt-text-muted)] mt-0.5">
                {isCrypto ? "Auto · incl 18% GST" : (tradingFeeIsAuto ? "Auto-estimated" : "Manual")}
              </p>
            </div>
            <div>
              <p className="bt-label">Funding Fee</p>
              <div
                data-testid="funding-fee-preview-value"
                className={`font-heading text-lg bt-num mt-1 ${
                  effectiveFundingFee > 0 ? "text-[var(--bt-danger)]" : "text-[var(--bt-text-muted)]"
                }`}
              >
                {effectiveFundingFee > 0
                  ? `-${formatMoney(effectiveFundingFee, form.currency)}`
                  : "—"}
              </div>
              <p className="text-[10px] text-[var(--bt-text-muted)] mt-0.5">
                {isCrypto ? "Manual" : "N/A"}
              </p>
            </div>
            <div>
              <p className="bt-label">Risk:Reward Ratio</p>
              <div
                data-testid="r-preview-value"
                className={`font-heading text-lg bt-num mt-1 ${
                  rPreview == null ? "text-[var(--bt-text-muted)]"
                  : rPreview >= 0 ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"
                }`}
              >
                {formatRRR(rPreview)}
              </div>
              <p className="text-[10px] text-[var(--bt-text-muted)] mt-0.5">Gross P&L ÷ Risk</p>
            </div>
          </div>
        </div>

        {error && (
          <div
            data-testid="trade-form-error"
            className="text-sm text-[var(--bt-danger)] border border-[var(--bt-danger)]/40 bg-[var(--bt-danger)]/10 px-3 py-2 rounded-sm flex items-center justify-between gap-3 flex-wrap"
          >
            <span>{error}</span>
            <button
              type="button"
              data-testid="trade-form-retry-btn"
              onClick={() => { setError(""); document.querySelector('[data-testid="trade-submit-btn"]')?.click(); }}
              className="text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-sm border border-[var(--bt-danger)] text-[var(--bt-danger)] hover:bg-[var(--bt-danger)]/15 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-2">
          <Button
            type="button" variant="outline"
            onClick={() => nav("/trades")}
            data-testid="trade-cancel-btn"
            className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] text-white rounded-sm h-11"
          >
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button
            type="submit" disabled={submitting}
            data-testid="trade-submit-btn"
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-11 px-6"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {submitting ? "Saving…" : isEdit ? "Update Trade" : "Save Trade"}
          </Button>
        </div>
      </form>
    </div>
  );
}
